/**
 * Service de gestion des notifications push PWA
 * Gère l'abonnement, le désabonnement et l'envoi de notifications push
 */

export interface PushSubscriptionData {
  user_id: number;
  endpoint: string;
  p256dh_key: string;
  auth_key: string;
  created_at?: string;
}

class PushNotificationService {
  private readonly vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

  /**
   * Vérifie si les notifications push sont supportées par le navigateur
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
  }

  /**
   * Vérifie si l'utilisateur a déjà donné la permission pour les notifications
   */
  hasPermission(): boolean {
    return Notification.permission === 'granted';
  }

  /**
   * Demande la permission à l'utilisateur pour les notifications
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Les notifications push ne sont pas supportées sur ce navigateur');
    }

    const permission = await Notification.requestPermission();
    console.log('📱 Permission notification:', permission);
    return permission;
  }

  /**
   * Convertit la clé VAPID base64 en Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * S'abonne aux notifications push
   */
  async subscribe(userId: number): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.isSupported()) {
        return { success: false, error: 'Notifications non supportées' };
      }

      if (!this.vapidPublicKey) {
        return { success: false, error: 'Clé VAPID non configurée' };
      }

      // Demander la permission si nécessaire
      if (Notification.permission === 'default') {
        const permission = await this.requestPermission();
        if (permission !== 'granted') {
          return { success: false, error: 'Permission refusée' };
        }
      }

      if (Notification.permission !== 'granted') {
        return { success: false, error: 'Permission requise' };
      }

      // Obtenir le service worker
      const registration = await navigator.serviceWorker.ready;

      // Vérifier si déjà abonné
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('📱 Déjà abonné aux notifications push');
        // Vérifier si la subscription existe dans la BD
        const saveResult = await this.ensureSubscriptionSaved(userId, existingSubscription);
        return saveResult;
      }

      // Créer une nouvelle subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      // Sauvegarder la subscription dans Supabase
      const subscriptionData: PushSubscriptionData = {
        user_id: userId,
        endpoint: subscription.endpoint,
        p256dh_key: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
        auth_key: this.arrayBufferToBase64(subscription.getKey('auth')!)
      };

      const saveResult = await this.saveSubscription(subscriptionData);
      if (!saveResult.success) {
        return saveResult;
      }

      console.log('✅ Abonné aux notifications push avec succès');
      return { success: true };

    } catch (error: any) {
      console.error('❌ Erreur lors de l\'abonnement aux notifications:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * S'assure que la subscription existante est sauvegardée dans la BD
   */
  private async ensureSubscriptionSaved(userId: number, subscription: PushSubscription): Promise<{ success: boolean; error?: string }> {
    try {
      const subscriptionData: PushSubscriptionData = {
        user_id: userId,
        endpoint: subscription.endpoint,
        p256dh_key: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
        auth_key: this.arrayBufferToBase64(subscription.getKey('auth')!)
      };

      return await this.saveSubscription(subscriptionData);
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde de la subscription:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Se désabonne des notifications push
   */
  async unsubscribe(userId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await this.removeSubscription(userId);
        console.log('✅ Désabonné des notifications push');
      }

      return { success: true };
    } catch (error: any) {
      console.error('❌ Erreur lors du désabonnement:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Vérifie si l'utilisateur est actuellement abonné
   */
  async isSubscribed(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('❌ Erreur vérification subscription:', error);
      return false;
    }
  }

  /**
   * Convertit ArrayBuffer en base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Sauvegarde la subscription dans Supabase
   */
  private async saveSubscription(data: PushSubscriptionData): Promise<{ success: boolean; error?: string }> {
    try {
      const { supabase } = await import('../supabase');
      
      // Vérifier si une subscription existe déjà pour cet utilisateur
      const { data: existing } = await supabase
        .from('push_subscriptions')
        .select('id')
        .eq('user_id', data.user_id)
        .single();

      if (existing) {
        // Mettre à jour la subscription existante
        const { error } = await supabase
          .from('push_subscriptions')
          .update({
            endpoint: data.endpoint,
            p256dh_key: data.p256dh_key,
            auth_key: data.auth_key,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', data.user_id);

        if (error) throw error;
      } else {
        // Créer une nouvelle subscription
        const { error } = await supabase
          .from('push_subscriptions')
          .insert(data);

        if (error) throw error;
      }

      return { success: true };
    } catch (error: any) {
      console.error('❌ Erreur sauvegarde subscription:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Supprime la subscription de Supabase
   */
  private async removeSubscription(userId: number): Promise<void> {
    try {
      const { supabase } = await import('../supabase');
      
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', userId);
    } catch (error) {
      console.error('❌ Erreur suppression subscription:', error);
    }
  }

  /**
   * Teste l'envoi d'une notification locale (sans passer par le serveur)
   */
  async sendTestNotification(): Promise<void> {
    if (!this.hasPermission()) {
      throw new Error('Permission requise');
    }

    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification('Hockey Dynasty Pool', {
      body: 'Les notifications push sont activées !',
      icon: '/image2.png',
      badge: '/image2.png',
      vibrate: [200, 100, 200],
      tag: 'test-notification',
      requireInteraction: false
    });
  }
}

export const pushNotificationService = new PushNotificationService();
